import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { calculateImposition } from '@/lib/imposition';
import {
  DEFAULT_MACHINE_CONSTRAINTS,
  DEFAULT_SOURCE_SHEETS,
  SheetCutOptimizer,
} from '@/lib/sheetCutOptimizer';
import SheetImposition from './SheetImposition';
import './SheetImposition.css';

const ImpositionSelector = ({
  value,
  onChange,
  defaultConfig = {},
  itemWidth,
  itemHeight,
  quantity,
  allowConfigEdit = true,
  enableSheetCutOptimizer = true,
  sourceSheets = DEFAULT_SOURCE_SHEETS,
  machineConstraints = DEFAULT_MACHINE_CONSTRAINTS,
  doubleSidedCountsAsExtraItems = true,
}) => {
  const [config, setConfig] = useState({
    sheetWidth: defaultConfig.sheetWidth ?? 52,
    sheetHeight: defaultConfig.sheetHeight ?? 74,
    gripperTop: defaultConfig.gripperTop ?? 2,
    gripperBottom: defaultConfig.gripperBottom ?? 0,
    marginLeft: defaultConfig.marginLeft ?? 0,
    marginRight: defaultConfig.marginRight ?? 0,
    bleed: defaultConfig.bleed ?? 0,
    separation: defaultConfig.separation ?? 0,
    spoilageSheets: defaultConfig.spoilageSheets ?? defaultConfig.spoilage_sheets ?? 50,
    allowRotation: defaultConfig.allowRotation ?? true,
    doubleSided: defaultConfig.doubleSided ?? false,
  });
  const [cutConfig, setCutConfig] = useState({
    maxWidth: machineConstraints.maxWidth ?? machineConstraints.max_width ?? DEFAULT_MACHINE_CONSTRAINTS.maxWidth,
    maxHeight: machineConstraints.maxHeight ?? machineConstraints.max_height ?? DEFAULT_MACHINE_CONSTRAINTS.maxHeight,
    minWidth: machineConstraints.minWidth ?? machineConstraints.min_width ?? DEFAULT_MACHINE_CONSTRAINTS.minWidth,
    minHeight: machineConstraints.minHeight ?? machineConstraints.min_height ?? DEFAULT_MACHINE_CONSTRAINTS.minHeight,
    allowRotation: false,
    step: machineConstraints.step ?? DEFAULT_MACHINE_CONSTRAINTS.step,
  });
  const [cutSortMode, setCutSortMode] = useState('waste');
  const cutOptimizer = useMemo(() => new SheetCutOptimizer(), []);

  const cutOptions = useMemo(() => {
    if (!enableSheetCutOptimizer) return [];
    if (!itemWidth || !itemHeight || itemWidth <= 0 || itemHeight <= 0) return [];

    return cutOptimizer.calculateBestCuts(sourceSheets, cutConfig)
      .map(cut => {
        const impositionOptions = calculateImposition({
          sheetWidth: Number(cut.generatedSheet.width),
          sheetHeight: Number(cut.generatedSheet.height),
          gripperTop: Number(config.gripperTop),
          gripperBottom: Number(config.gripperBottom),
          marginLeft: Number(config.marginLeft),
          marginRight: Number(config.marginRight),
          itemWidth: Number(itemWidth),
          itemHeight: Number(itemHeight),
          bleed: Number(config.bleed),
          separation: Number(config.separation),
          allowRotation: Boolean(config.allowRotation),
          doubleSided: Boolean(config.doubleSided),
          quantity: Number(quantity) || 1,
        });

        if (impositionOptions.length === 0) return null;

        const bestImposition = impositionOptions[0];
        const sourceArea = cut.sourceSheet.width * cut.sourceSheet.height;
        const cutUsedArea = cut.sheetCount * cut.generatedSheet.width * cut.generatedSheet.height;
        const printableArea = Math.max(0, cut.generatedSheet.width - Number(config.marginLeft) - Number(config.marginRight))
          * Math.max(0, cut.generatedSheet.height - Number(config.gripperTop) - Number(config.gripperBottom));
        const internalWastePercent = Math.max(0, 100 - Number(bestImposition.usage || 0));
        const internalWasteArea = cut.sheetCount * printableArea * (internalWastePercent / 100);
        const totalWasteArea = Math.max(0, cut.sourceSheet.width * cut.sourceSheet.height - cutUsedArea) + internalWasteArea;
        const totalWastePercent = sourceArea > 0 ? (totalWasteArea / sourceArea) * 100 : 100;

        return {
          ...cut,
          bestImposition,
          internalWastePercent,
          totalWasteArea,
          totalWastePercent,
        };
      })
      .filter(Boolean);
  }, [
    enableSheetCutOptimizer,
    cutOptimizer,
    sourceSheets,
    cutConfig,
    config,
    itemWidth,
    itemHeight,
    quantity,
  ]);

  const sortedCutOptions = useMemo(() => {
    const sorted = [...cutOptions].sort((a, b) => {
      if (cutSortMode === 'sheetCount') {
        return (
          b.sheetCount - a.sheetCount
          || a.totalWastePercent - b.totalWastePercent
          || b.bestImposition.items_per_sheet - a.bestImposition.items_per_sheet
          || a.sourceSheet.label.localeCompare(b.sourceSheet.label)
          || a.generatedSheet.label.localeCompare(b.generatedSheet.label)
        );
      }

      return (
        a.totalWastePercent - b.totalWastePercent
        || a.wastePercent - b.wastePercent
        || b.bestImposition.items_per_sheet - a.bestImposition.items_per_sheet
        || b.sheetCount - a.sheetCount
        || a.sourceSheet.label.localeCompare(b.sourceSheet.label)
        || a.generatedSheet.label.localeCompare(b.generatedSheet.label)
      );
    });

    const bestBySourceSheet = [];
    const seenSourceSheets = new Set();

    sorted.forEach((option) => {
      const sourceKey = `${option.sourceSheet.width}x${option.sourceSheet.height}`;
      if (seenSourceSheets.has(sourceKey)) return;
      seenSourceSheets.add(sourceKey);
      bestBySourceSheet.push(option);
    });

    return bestBySourceSheet.slice(0, 18);
  }, [cutOptions, cutSortMode]);

  const [selectedCutId, setSelectedCutId] = useState(value?.config?.sourceCut?.id || null);

  useEffect(() => {
    if (!enableSheetCutOptimizer || sortedCutOptions.length === 0) {
      setSelectedCutId(null);
      return;
    }
    if (!selectedCutId || !sortedCutOptions.find(o => o.id === selectedCutId)) {
      setSelectedCutId(sortedCutOptions[0].id);
    }
  }, [enableSheetCutOptimizer, sortedCutOptions, selectedCutId]);

  const selectedCut = sortedCutOptions.find(o => o.id === selectedCutId) || null;
  const activeConfig = useMemo(() => ({
    ...config,
    sheetWidth: selectedCut ? selectedCut.generatedSheet.width : config.sheetWidth,
    sheetHeight: selectedCut ? selectedCut.generatedSheet.height : config.sheetHeight,
    sourceCut: selectedCut,
  }), [config, selectedCut]);

  const options = useMemo(() => {
    if (!itemWidth || !itemHeight || itemWidth <= 0 || itemHeight <= 0) return [];
    return calculateImposition({
      sheetWidth: Number(activeConfig.sheetWidth),
      sheetHeight: Number(activeConfig.sheetHeight),
      gripperTop: Number(activeConfig.gripperTop),
      gripperBottom: Number(activeConfig.gripperBottom),
      marginLeft: Number(activeConfig.marginLeft),
      marginRight: Number(activeConfig.marginRight),
      itemWidth: Number(itemWidth),
      itemHeight: Number(itemHeight),
      bleed: Number(activeConfig.bleed),
      separation: Number(activeConfig.separation),
      allowRotation: Boolean(activeConfig.allowRotation),
      doubleSided: Boolean(activeConfig.doubleSided),
      quantity: Number(quantity) || 1,
    });
  }, [activeConfig, itemWidth, itemHeight, quantity]);

  const [selectedId, setSelectedId] = useState(value?.id || null);

  // Si no hay selección o la actual no está entre las opciones, elegir la primera (óptima)
  useEffect(() => {
    if (options.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !options.find(o => o.id === selectedId)) {
      setSelectedId(options[0].id);
    }
  }, [options, selectedId]);

  const selected = options.find(o => o.id === selectedId);

  // Notificar cambio hacia arriba sin loops
  const lastNotifiedRef = useRef('');
  useEffect(() => {
    const key = selected
      ? `${selected.id}-${selected.items_per_sheet}-${JSON.stringify(activeConfig)}`
      : 'null';
    if (lastNotifiedRef.current !== key) {
      lastNotifiedRef.current = key;
      onChange(selected || null, activeConfig);
    }
  }, [selected, activeConfig, onChange]);

  const updateConfig = (patch) => setConfig(c => ({ ...c, ...patch }));
  const updateCutConfig = (patch) => setCutConfig(c => ({ ...c, ...patch }));
  const getEffectiveItemsPerSheet = useCallback((option) => {
    const frontItems = Number(option?.items_per_sheet) || 0;
    return frontItems * (activeConfig.doubleSided && doubleSidedCountsAsExtraItems ? 2 : 1);
  }, [activeConfig.doubleSided, doubleSidedCountsAsExtraItems]);

  // Calcular producidas y excedente
  const producedInfo = useMemo(() => {
    if (!selected) return null;
    const frontItemsPerSheet = Number(selected.items_per_sheet) || 0;
    const perSheet = getEffectiveItemsPerSheet(selected);
    const sheets = perSheet > 0 ? Math.ceil((Number(quantity) || 0) / perSheet) : selected.sheets_needed;
    const generatedSheetsPerMother = Math.max(Number(activeConfig.sourceCut?.sheetCount) || 1, 1);
    const spoilageMotherSheets = Math.max(0, Math.ceil(Number(activeConfig.spoilageSheets) || 0));
    const motherSheets = Math.ceil(sheets / generatedSheetsPerMother);
    const totalMotherSheets = motherSheets + spoilageMotherSheets;
    const totalSheets = sheets + (spoilageMotherSheets * generatedSheetsPerMother);
    const produced = perSheet * sheets;
    const extra = produced - Number(quantity || 0);
    const printableArea = (activeConfig.sheetWidth - activeConfig.marginLeft - activeConfig.marginRight)
      * (activeConfig.sheetHeight - activeConfig.gripperTop - activeConfig.gripperBottom);
    const piecesArea = frontItemsPerSheet * (Number(itemWidth) + 2 * Number(config.bleed))
      * (Number(itemHeight) + 2 * Number(config.bleed));
    const waste = printableArea > 0 ? 100 - (piecesArea / printableArea * 100) : 0;
    return {
      produced,
      extra,
      waste: Math.max(0, waste),
      sheets,
      totalSheets,
      motherSheets,
      totalMotherSheets,
      spoilageMotherSheets,
      generatedSheetsPerMother,
      frontItemsPerSheet,
      effectiveItemsPerSheet: perSheet,
    };
  }, [selected, quantity, config, activeConfig, itemWidth, itemHeight, getEffectiveItemsPerSheet]);

  return (
    <div className="imposition-container" data-testid="imposition-selector">
      {enableSheetCutOptimizer && (
        <div className="sheet-cut-stage" data-testid="sheet-cut-optimizer">
          <div className="imposition-result-header">
            <span className="imposition-result-title">Corte de hoja madre</span>
            {selectedCut && (
              <span className="imposition-recommended-badge">
                {(100 - selectedCut.totalWastePercent).toFixed(1)}%
              </span>
            )}
          </div>

          <div className="sheet-cut-sort">
            <span className="form-label">Ordenar por</span>
            <div className="sheet-cut-sort-buttons">
              <button
                type="button"
                className={`sheet-cut-sort-button ${cutSortMode === 'waste' ? 'selected' : ''}`}
                onClick={() => setCutSortMode('waste')}
              >
                Menor desperdicio
              </button>
              <button
                type="button"
                className={`sheet-cut-sort-button ${cutSortMode === 'sheetCount' ? 'selected' : ''}`}
                onClick={() => setCutSortMode('sheetCount')}
              >
                Mas pliegos
              </button>
            </div>
          </div>

          <div className="imposition-controls sheet-cut-controls">
            <div className="form-group">
              <label className="form-label">Max. ancho maquina</label>
              <input type="number" className="form-input mono" value={cutConfig.maxWidth}
                onChange={(e) => updateCutConfig({ maxWidth: e.target.value })} step="0.1" />
            </div>
            <div className="form-group">
              <label className="form-label">Max. alto maquina</label>
              <input type="number" className="form-input mono" value={cutConfig.maxHeight}
                onChange={(e) => updateCutConfig({ maxHeight: e.target.value })} step="0.1" />
            </div>
            <div className="form-group">
              <label className="form-label">Min. ancho maquina</label>
              <input type="number" className="form-input mono" value={cutConfig.minWidth}
                onChange={(e) => updateCutConfig({ minWidth: e.target.value })} step="0.1" />
            </div>
            <div className="form-group">
              <label className="form-label">Min. alto maquina</label>
              <input type="number" className="form-input mono" value={cutConfig.minHeight}
                onChange={(e) => updateCutConfig({ minHeight: e.target.value })} step="0.1" />
            </div>
            <div className="form-group">
              <label className="form-label">Resolucion corte</label>
              <input type="number" className="form-input mono" value={cutConfig.step}
                onChange={(e) => updateCutConfig({ step: e.target.value })} step="0.1" min="0.1" />
            </div>
          </div>

          {selectedCut ? (
            <>
              <div className="sheet-cut-selected">
                <div className="imposition-result-row">
                  <span>Hoja madre</span>
                  <strong className="mono">{selectedCut.sourceSheet.label} cm</strong>
                </div>
                <div className="imposition-result-row">
                  <span>Pliego generado</span>
                  <strong className="mono">{selectedCut.generatedSheet.label} cm</strong>
                </div>
                <div className="imposition-result-row">
                  <span>Pliegos por hoja madre</span>
                  <strong className="mono">{selectedCut.sheetCount}</strong>
                </div>
                <div className="imposition-result-row">
                  <span>Desperdicio de corte</span>
                  <strong className="mono result-warn">{selectedCut.wastePercent.toFixed(1)}%</strong>
                </div>
                <div className="imposition-result-row">
                  <span>Desperdicio total</span>
                  <strong className="mono result-warn">{selectedCut.totalWastePercent.toFixed(1)}%</strong>
                </div>
              </div>

              <div className="sheet-cut-options">
                {sortedCutOptions.map(opt => {
                  const isSel = selectedCut && opt.id === selectedCut.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      className={`sheet-cut-option ${isSel ? 'selected' : ''}`}
                      onClick={() => setSelectedCutId(opt.id)}
                    >
                      <span className="mono">{opt.sourceSheet.label} -&gt; {opt.generatedSheet.label}</span>
                      <span className="mono">
                        {opt.sheetCount} pl. - corte {opt.wastePercent.toFixed(1)}% - total {opt.totalWastePercent.toFixed(1)}%
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="imposition-no-fit">
              No hay cortes validos para el stock y las restricciones de maquina configuradas.
            </div>
          )}
        </div>
      )}

      {allowConfigEdit && (
        <div className="imposition-controls">
          <div className="form-group">
            <label className="form-label">Ancho pliego (cm)</label>
            <input type="number" className="form-input mono" value={activeConfig.sheetWidth}
              disabled={Boolean(selectedCut)}
              onChange={(e) => updateConfig({ sheetWidth: e.target.value })} step="0.1" data-testid="imp-sheet-width" />
          </div>
          <div className="form-group">
            <label className="form-label">Alto pliego (cm)</label>
            <input type="number" className="form-input mono" value={activeConfig.sheetHeight}
              disabled={Boolean(selectedCut)}
              onChange={(e) => updateConfig({ sheetHeight: e.target.value })} step="0.1" data-testid="imp-sheet-height" />
          </div>
          <div className="form-group">
            <label className="form-label">Pinza sup. (cm)</label>
            <input type="number" className="form-input mono" value={config.gripperTop}
              onChange={(e) => updateConfig({ gripperTop: e.target.value })} step="0.1" data-testid="imp-gripper-top" />
          </div>
          <div className="form-group">
            <label className="form-label">Pinza inf. (cm)</label>
            <input type="number" className="form-input mono" value={config.gripperBottom}
              onChange={(e) => updateConfig({ gripperBottom: e.target.value })} step="0.1" />
          </div>
          <div className="form-group">
            <label className="form-label">Sangrado (cm)</label>
            <input type="number" className="form-input mono" value={config.bleed}
              onChange={(e) => updateConfig({ bleed: e.target.value })} step="0.1" data-testid="imp-bleed" />
          </div>
          <div className="form-group">
            <label className="form-label">Separación (cm)</label>
            <input type="number" className="form-input mono" value={config.separation}
              onChange={(e) => updateConfig({ separation: e.target.value })} step="0.1" />
          </div>
          <div className="form-group">
            <label className="form-label">Desperdicio (hojas madre)</label>
            <input type="number" className="form-input mono" value={config.spoilageSheets}
              onChange={(e) => updateConfig({ spoilageSheets: e.target.value })} step="1" min="0" />
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <label className="checkbox-label">
              <input type="checkbox" checked={config.allowRotation}
                onChange={(e) => updateConfig({ allowRotation: e.target.checked })} data-testid="imp-allow-rotation" />
              {' '}Rotación
            </label>
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <label className="checkbox-label">
              <input type="checkbox" checked={config.doubleSided}
                onChange={(e) => updateConfig({ doubleSided: e.target.checked })} data-testid="imp-double-sided" />
              {' '}Doble faz
            </label>
          </div>
        </div>
      )}

      {options.length === 0 ? (
        <div className="imposition-no-fit" data-testid="imposition-no-fit">
          Las piezas no entran en el pliego con la configuración actual. Ajustá dimensiones o márgenes.
        </div>
      ) : (
        <div className="imposition-layout">
          {/* ===== Vista principal ===== */}
          <div className="imposition-main">
            {selected && (
              <>
                <SheetImposition
                  sheetWidth={Number(activeConfig.sheetWidth)}
                  sheetHeight={Number(activeConfig.sheetHeight)}
                  gripperTop={Number(activeConfig.gripperTop)}
                  gripperBottom={Number(activeConfig.gripperBottom)}
                  marginLeft={Number(activeConfig.marginLeft)}
                  marginRight={Number(activeConfig.marginRight)}
                  pieces={selected.pieces}
                  itemWidth={Number(itemWidth)}
                  itemHeight={Number(itemHeight)}
                  bleed={Number(activeConfig.bleed)}
                  side="front"
                  maxWidth={560}
                />
                {activeConfig.doubleSided && (
                  <SheetImposition
                    sheetWidth={Number(activeConfig.sheetWidth)}
                    sheetHeight={Number(activeConfig.sheetHeight)}
                    gripperTop={Number(activeConfig.gripperTop)}
                    gripperBottom={Number(activeConfig.gripperBottom)}
                    marginLeft={Number(activeConfig.marginLeft)}
                    marginRight={Number(activeConfig.marginRight)}
                    pieces={selected.pieces}
                    itemWidth={Number(itemWidth)}
                    itemHeight={Number(itemHeight)}
                    bleed={Number(activeConfig.bleed)}
                    side="back"
                    maxWidth={560}
                    showLegend={false}
                  />
                )}
              </>
            )}
          </div>

          {/* ===== Panel resultado óptimo + alternativas ===== */}
          <div className="imposition-sidebar">
            {selected && (
              <div className="imposition-result-card">
                <div className="imposition-result-header">
                  <span className="imposition-result-title">Resultado</span>
                  <span className="imposition-recommended-badge">Elegida</span>
                </div>
                <div className="imposition-result-rows">
                  <div className="imposition-result-row">
                    <span>Opción</span>
                    <strong>{selected.label}</strong>
                  </div>
                  <div className="imposition-result-row">
                    <span>Piezas por pliego</span>
                    <strong className="mono" data-testid="result-items-per-sheet">
                      {producedInfo?.effectiveItemsPerSheet || selected.items_per_sheet} u.
                    </strong>
                  </div>
                  <div className="imposition-result-row">
                    <span>Pliegos base</span>
                    <strong className="mono" data-testid="result-sheets-needed">
                      {producedInfo?.sheets || selected.sheets_needed}
                    </strong>
                  </div>
                  {producedInfo && (
                    <>
                      <div className="imposition-result-row">
                        <span>Desperdicio operativo</span>
                        <strong className="mono result-warn">{producedInfo.spoilageMotherSheets} hojas madre</strong>
                      </div>
                      <div className="imposition-result-row">
                        <span>Total para precio</span>
                        <strong className="mono result-good">{producedInfo.totalSheets} pliegos / {producedInfo.totalMotherSheets} hojas</strong>
                      </div>
                      <div className="imposition-result-row">
                        <span>Piezas totales</span>
                        <strong className="mono">{producedInfo.produced} u.</strong>
                      </div>
                      <div className="imposition-result-row">
                        <span>Aprovechamiento</span>
                        <strong className="mono result-good">{(100 - producedInfo.waste).toFixed(1)}%</strong>
                      </div>
                      <div className="imposition-result-row">
                        <span>Desperdicio</span>
                        <strong className="mono result-warn">{producedInfo.waste.toFixed(1)}%</strong>
                      </div>
                    </>
                  )}
                </div>
                {producedInfo && producedInfo.extra > 0 && (
                  <div className="imposition-info-note">
                    Se producen {producedInfo.extra} unidades extra
                  </div>
                )}
              </div>
            )}

            {options.length > 1 && (
              <div className="imposition-alternatives">
                <div className="imposition-alternatives-title">Alternativas</div>
                {options.map(opt => {
                  const isSel = selected && opt.id === selected.id;
                  const effectiveItems = getEffectiveItemsPerSheet(opt);
                  const effectiveSheets = effectiveItems > 0
                    ? Math.ceil((Number(quantity) || 0) / effectiveItems)
                    : opt.sheets_needed;
                  return (
                    <div
                      key={opt.id}
                      className={`imposition-alt-card ${isSel ? 'selected' : ''}`}
                      onClick={() => setSelectedId(opt.id)}
                      data-testid={`imposition-option-${opt.id}`}
                    >
                      <div className="imposition-alt-header">
                        <span className="imposition-alt-title">{opt.label}</span>
                        {isSel && <span className="imposition-recommended-badge small">•</span>}
                      </div>
                      <div className="imposition-alt-stats">
                        <div>
                          <div className="imposition-alt-stat-val mono" data-testid={`imp-items-${opt.id}`}>{effectiveItems || opt.items_per_sheet}</div>
                          <div className="imposition-alt-stat-lbl">Por pliego</div>
                        </div>
                        <div>
                          <div className="imposition-alt-stat-val mono" data-testid={`imp-sheets-${opt.id}`}>{effectiveSheets}</div>
                          <div className="imposition-alt-stat-lbl">Pliegos</div>
                        </div>
                        <div>
                          <div className="imposition-alt-stat-val mono">{opt.usage.toFixed(0)}%</div>
                          <div className="imposition-alt-stat-lbl">Aprov.</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImpositionSelector;
